import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import { useLocalizedPath } from "@/hooks/useLocalizedPath";
import usePageContent from "@/hooks/usePageContent";
import heroBg from "@/assets/hero-bg.jpg";
import logoPng from "@/assets/firststage-logo.png";

const HeroSection = () => {
  const { t } = useTranslation();
  const { localizedPath } = useLocalizedPath();
  const { getContent } = usePageContent("home");
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      // Only track scroll within the hero section height for performance
      if (window.scrollY < window.innerHeight * 1.5) {
        setScrollY(window.scrollY);
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Calculate parallax offset (moves slower than scroll)
  const parallaxOffset = scrollY * 0.4;

  return (
    <section 
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
      aria-label={t("hero.ariaLabel", "Welcome to First Stage Talent")}
    >
      {/* Parallax Background */}
      <div 
        className="absolute inset-0 w-full h-[120%] -top-[10%] grayscale"
        style={{
          backgroundImage: `url(${heroBg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          transform: `translateY(${parallaxOffset}px)`,
          willChange: "transform",
        }}
      />
      {/* Overlay */}
      <div className="absolute inset-0 bg-primary/50" />
      
      {/* Content */}
      <div className="relative z-10 container-wide text-center px-5 sm:px-6">
        <div className="max-w-4xl mx-auto">
          {/* Logo */}
          <div className="flex justify-center mb-12 sm:mb-14 md:mb-16 animate-fade-up pt-6 sm:pt-8 md:pt-0">
            <img 
              src={logoPng} 
              alt="First Stage Talent" 
              className="h-20 sm:h-28 md:h-36 lg:h-44 w-auto"
            />
          </div>
          
          {/* Headline */}
          <h1 className="text-[1.625rem] leading-tight sm:text-3xl md:text-4xl lg:text-5xl font-display font-bold tracking-tight text-white mb-4 sm:mb-5 md:mb-6 animate-fade-up delay-100 whitespace-pre-line">
            {getContent("hero_title", t("hero.title"))}
          </h1>
          
          {/* Subheadline */}
          <p className="text-[0.9375rem] leading-relaxed sm:text-lg md:text-xl text-white/85 mb-8 sm:mb-10 md:mb-12 max-w-2xl mx-auto animate-fade-up delay-200 font-light whitespace-pre-line">
            {getContent("hero_subtitle", t("hero.subtitle"))}
          </p>
          
          {/* CTAs */}
          <div className="flex flex-col gap-3 sm:flex-row sm:gap-4 justify-center animate-fade-up delay-300">
            <Button asChild variant="hero" size="hero" className="bg-white text-primary hover:bg-white/90 min-h-[52px] w-full sm:w-auto">
              <Link to={localizedPath("/talent")}>{getContent("hero_view_talent", t("hero.viewTalent"))}</Link>
            </Button>
            <Button asChild variant="heroOutline" size="hero" className="min-h-[52px] w-full sm:w-auto">
              <Link to={localizedPath("/services")}>{getContent("hero_our_services", t("hero.ourServices"))}</Link>
            </Button>
          </div>
        </div>
      </div>

    </section>
  );
};

export default HeroSection;
